<template>
  <transition name="fade" mode="out-in">
    <div>
      <h1 class="text-lg mt-10 mb-0 text-center text-black">
        <template v-if="mode === 'signup'">
          Welcome to Digital Risks!
        </template>
        <template v-else>
          Welcome back to Digital Risks!
        </template>
      </h1>

      <p class="text-2xs text-center text-black">
        <template v-if="mode === 'signup'">
          You’re just a few clicks away from getting your personalised quote tailored to your business.
        </template>
        <template v-else>
          Please sign in to continue.
        </template>
      </p>

      <form @submit.prevent="handleSubmit">
        <DrInput
          id="email"
          v-model="email"
          :error="errors.email"
          placeholder="email address"
          icon="email"
          class="mb-0"
        />

        <div v-if="mode === 'signup' && alreadyHaveAccount && !errors.email" class="error pt-1 flex items-center">
          <DrIcon svg="close-solid" class="mr-1" />
          <small>This email already has an account. Please
            <a href="#" @click.prevent="mode = 'signin'"><strong>Sign In</strong></a></small>
        </div>

        <template v-if="mode === 'signup'">
          <AppFieldUserPassword
            v-model="password"
            placeholder="******"
            :show-password="showPassword"
            :error="errors.password"
            class="my-3"
            @valid="passwordValid = $event"
          />
          <p
            class="mb-4 show-password text-2xs font-bold text-red-500 text-right cursor-pointer select-none"
            @click="showPassword = !showPassword"
          >
            <span v-if="showPassword">Hide password</span>
            <span v-else>Show password</span>
          </p>
        </template>
        <template v-else>
          <DrInput
            id="password"
            v-model="password"
            type="password"
            placeholder="******"
            :error="errors.password"
            class="my-3"
          />
          <p class="text-2xs text-right font-bold text-red-500 mb-3">
            <RouterLink :to="{ name: 'user-reset-password' }" class="no-underline">Forgot password?</RouterLink>
          </p>
        </template>

        <DrInputCheckbox v-if="$route.params.partnerId" v-model="agreePartner" class="mb-4">
          <span class="-mt-1 text-2xs text-grey-500 leading-snug">
            I agree to allowing {{ getPartnerNameFromId($route.params.partnerId) }} to access my list of policies. No
            other data will be shared.
          </span>
        </DrInputCheckbox>

        <div v-if="errors && errors.terms" class="error pt-2 flex items-center">
          <span class="text-red-500 text-xs font-semibold relative" style="top: -24px;">
            <DrIcon svg="close-solid" class="mr-1" /> {{ errors.terms }}
          </span>
        </div>

        <DrButton
          class="btn-cyan w-full text-xs rounded-full"
          type="submit"
          :loading="loading === 'user-register' || loading === 'user-signin'"
        >
          <template v-if="mode === 'signup'">
            Create Account
          </template>
          <template v-else>
            Sign in
          </template>
        </DrButton>

        <p class="mt-6 text-sm font-semibold text-center text-grey-500">
          <template
            v-if="mode === 'signup'"
          >
            Have an account?
          </template>
          <template v-else>
            Don't have an account?
          </template>
          <a href="#" class="no-underline text-blue-500 cursor-pointer" @click.prevent="handleSwitchLink">
            <template v-if="mode === 'signup'">
              Sign in
            </template>
            <template v-else>
              Register
            </template>
          </a>
        </p>
      </form>

      <p v-if="mode === 'signup'" class="-mt-1 text-2xs text-grey-500 leading-snug text-center">
        By creating a Digital Risks account, you agree to our
        <a
          class="text-blue-500 no-underline"
          href="https://www.digitalrisks.co.uk/terms-and-conditions/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of Service</a>,
        <a class="text-blue-500 no-underline" href="https://www.digitalrisks.co.uk/privacy-notice/" target="_blank" rel="noopener noreferrer">
          Privacy Policy</a>
        and
        <a
          class="text-blue-500 no-underline"
          href="https://www.digitalrisks.co.uk/privacy-notice/#use-of-cookies"
          target="_blank"
          rel="noopener noreferrer"
        >
          Cookie Policy</a>.
      </p>
    </div>
  </transition>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/auth'

import AppFieldUserPassword from '@/components/user/fields/Password.vue'

export default {
  components: {
    AppFieldUserPassword,
  },
  data () {
    return {
      mode: 'signup',
      errors: {},
      email: '',
      password: '',
      agreePartner: false,
      // Register Form
      passwordValid: false,
      alreadyHaveAccount: false,
      magicLinkSent: false,
      showPassword: false,
    }
  },
  computed: {
    user () {
      return this.$store.getters['user/user']
    },
    loading () {
      return this.$store.getters['shared/loading']
    },
    error () {
      return this.$store.getters['shared/error']
    },
  },
  watch: {
    '$route.query.u' (val) {
      this.mode = val === 'signin' ? 'signin' : 'signup'
    },
    async user (value) {
      if (!value) return

      const user = firebase.auth().currentUser
      await user.getIdToken()

      this.$emit('loggedIn', true)
    },
    error (error) {
      this.errors = {}
      // Customise the firebase error messages
      // https://firebase.google.com/docs/auth/admin/errors
      if (error && error.code) {
        switch (error.code) {
          case 'auth/invalid-email':
            this.errors.email = 'Invalid email'
            break
          case 'auth/wrong-password':
          case 'auth/user-not-found':
            this.errors.password = 'Incorrect password'
            break
          case 'auth/weak-password':
            this.handleError('password', 'Password is too weak.')
            break
          case 'auth/email-already-in-use':
            this.alreadyHaveAccount = true
            break
        }
      }

      if (error && error.message) {
        switch (error.message) {
          case 'The email address is badly formatted.':
            this.handleError('email', 'Invalid email')
            break
          case 'Terms not agreed':
            this.handleError('terms', 'You must agree to the terms.')
            break
        }
      }
    },
  },
  mounted () {
    // Reset errors
    this.$store.dispatch('shared/clearError')

    if (this.mode === 'signup') {
      this.switchLink = {
        text: 'Have an account?',
        linkText: 'Sign in',
      }
    }

    if (this.$route.query.u === 'signin') this.mode = 'signin'

    if (this.$route.params.partnerId === 'starling') {
      this.$store.commit('user/setLoginFlow', { partner: 'starling', state: this.$route.query.state })
    }
  },
  detroyed () {
    // Reset errors
    this.$store.dispatch('shared/clearError')
  },
  methods: {
    handleSubmit () {
      if (this.mode === 'signup') return this.onSignUp()
      return this.onSignIn()
    },
    onSignIn () {
      if (this.$route.params.partnerId === 'starling' && !this.agreePartner) {
        this.$store.commit('shared/setError', { message: 'Terms not agreed' })
      } else {
        this.$store.dispatch('user/signUserIn', { email: this.email, password: this.password })
      }
    },
    onSignUp () {
      if (this.validate()) {
        // Standard email + password sign up
        this.$store.dispatch('user/signUserUp', {
          email: this.email,
          password: this.password,
        })
      }
    },
    validate () {
      this.errors = {}
      if (!this.email) {
        this.$store.commit('shared/setError', { message: 'The email address is badly formatted.' })
        return false
      } else if (!this.passwordValid) {
        this.$store.commit('shared/setError', { code: 'auth/weak-password' })
        return false
      } else if (this.$route.params.partnerId && !this.agreePartner) {
        this.$store.commit('shared/setError', { message: 'Terms not agreed' })
        return false
      }
      return true
    },
    handleSwitchLink () {
      // Not using different route to make oAuth dance easier
      this.mode = this.mode === 'signup' ? 'signin' : 'signup'
    },
    getPartnerNameFromId (id) {
      const partners = {
        starling: 'Starling Bank',
      }
      return partners[id]
    },
    handleError (field, message) {
      this.errors[field] = message
    },
    getIdToken () {
      const user = firebase.auth().currentUser
      user.getIdToken().then((token) => {
        return token
      })
    },
  },
}
</script>

<style scoped>
/* stylelint-disable */
.btn-sign-in::v-deep span {
  @apply flex items-center justify-center p-px;
}
/* stylelint-enable */
</style>
